import { useState, useEffect, useRef } from 'react';
import { faCircleArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DRButton } from '../components/DRControls';
import './ArrivalScreen.scss';
import Dialog from '../components/Dialog';
import { getConfig } from '../utils/configUtils';
import { ScanResult } from '../constants/containerManagerInterfaces';
import {noteInteraction} from '../vodjs/Utils';

interface ArrivalScreenProps {
  mainText: string;
  noteText: string;
  requestedBy: string;
  waiting: boolean;
  clickButton: () => void;
  showButton: boolean;
  showScanPrompt: boolean;
  showPleaseWait: boolean;
  textDisplayMode: boolean;
  tapToReload: () => void;
  scanResult: string;
  lastScanTimestamp: string;
  isRestricted: boolean;
  givenToText: string;
}

const usePrevious = (value: string) => {
  const ref = useRef<string>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export default function ArrivalScreen(props: ArrivalScreenProps) {
  const {
    mainText,
    noteText,
    requestedBy,
    waiting,
    clickButton,
    showButton,
    showScanPrompt,
    showPleaseWait,
    textDisplayMode,
    tapToReload,
    scanResult,
    lastScanTimestamp,
    isRestricted,
    givenToText,
  } = props;
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const lastScan = usePrevious(lastScanTimestamp);
  const supportNumber = getConfig().site?.supportNumber;
  const secureDeliveryEnabled = getConfig().secureDelivery;

  useEffect(() => {
    if (lastScan !== lastScanTimestamp) {
      setShowBadgeModal(scanResult === ScanResult.Prohibited);
    }
  }, [scanResult, lastScan, lastScanTimestamp]);

  const showProhibitedModal = () => {
    return showBadgeModal && secureDeliveryEnabled && isRestricted;
  };

  return (
    <div id="arrival-screen" className="arrival-screen">
      {!waiting && (
        <>
          <div className="arrival-screen__header">
            <img
              src={require('../assets/images/welcome_diligent_logo.png')}
              height={48}
              width={121}
              onClick={() => {noteInteraction(); tapToReload(); }}
              alt="Diligent"
            />
          </div>

          <div className="arrival-screen__content">
            {!textDisplayMode && <span className="content__greeting">Hello, I'm here for a</span>}
            <div className={`content__text-wrapper`}>
              <span className={`arrival-screen__text${textDisplayMode ? '' : ' arrival-screen__text--line-clamp-2'}`}>
                {mainText}
              </span>
            </div>
            {!isRestricted && noteText && (
              <div className="content__note-wrapper">
                <span className="content__note">{noteText.trim()}</span>
              </div>
            )}
            {givenToText && (
              <div className="content__note-wrapper">
                <span className="content__note">{givenToText}</span>
              </div>
            )}
            {!textDisplayMode && requestedBy && (
              <div className="content__requested-by-wrapper">
                <span className="content__requested-by">Requested by </span>
                <span className="content__requested-by content__requested-by--emph">{requestedBy}</span>
              </div>
            )}
          </div>

          <div className="arrival-screen__footer">
            {showButton && (
              <div id="continue" className="footer__continue-wrapper">
                <DRButton
                  action={() => clickButton()}
                  phrase={'CONTINUE'}
                  fontColor={'white'}
                  fillColor={'transparent'}
                  borderColor={'white'}
                />
              </div>
            )}

            {showPleaseWait && <span className="arrival-screen__text">Please wait...</span>}

            {showScanPrompt && (
              <div className="footer__scan-badge-wrapper">
                <span className="scan-badge__text">Scan your badge below to begin.</span>
                <FontAwesomeIcon icon={faCircleArrowDown} size={'3x'} color={'white'} />
              </div>
            )}
          </div>

          {showProhibitedModal() && (
            <Dialog
              title="You can't access this delivery."
              text={`Your badge doesn't have permission to access what this Moxi is
              carrying. Contact Moxi support ${supportNumber ? `at ${supportNumber} ` : ''}for help.`}
              primaryButtonText="OKAY"
              primaryButtonColor={'#0091ae'}
              primaryButtonWidth={220}
              clickPrimaryButton={() => setShowBadgeModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
